export const GAME_ASSET_VERSION = "game-production-v9" as const;
export const GAME_ICON_SPRITE_PATH =
  "/assets/game/game-icons-v1.svg" as const;
export const DUNGEON_SPRITE_PATH =
  "/assets/game/dungeon-sprites-v3.svg" as const;
export const DUNGEON_ENVIRONMENT_PATH =
  "/assets/game/dungeon-echo-vault-chamber-v2.webp" as const;

export const DUNGEON_ENVIRONMENT_ASSETS = {
  emberCrypt: {
    desktop: "/assets/game/dungeon-ember-crypt-chamber-v2.webp",
    mobile: "/assets/game/dungeon-ember-crypt-chamber-mobile-v2.webp",
  },
  floodedReliquary: {
    desktop: "/assets/game/dungeon-flooded-reliquary-chamber-v2.webp",
    mobile: "/assets/game/dungeon-flooded-reliquary-chamber-mobile-v2.webp",
  },
  echoVault: {
    desktop: DUNGEON_ENVIRONMENT_PATH,
    mobile: "/assets/game/dungeon-echo-vault-chamber-mobile-v2.webp",
  },
} as const;

export const DUNGEON_CHARACTER_ASSETS = {
  hero: "/assets/game/dungeon-hero-v4.webp",
  brute: "/assets/game/dungeon-monster-brute-v4.webp",
  raider: "/assets/game/dungeon-monster-raider-v4.webp",
  stalker: "/assets/game/dungeon-monster-stalker-v4.webp",
  warden: "/assets/game/dungeon-monster-warden-v4.webp",
  cinderHounds: "/assets/game/dungeon-monster-cinder-hounds-v1.webp",
  glassOracle: "/assets/game/dungeon-monster-glass-oracle-v1.webp",
  mireArbalist: "/assets/game/dungeon-monster-mire-arbalist-v1.webp",
  obsidianShieldbearer: "/assets/game/dungeon-monster-obsidian-shieldbearer-v1.webp",
  gatehouseFencer: "/assets/game/dungeon-monster-gatehouse-fencer-v1.webp",
  hollowLanternMage: "/assets/game/dungeon-monster-hollow-lantern-mage-v1.webp",
  chainWarden: "/assets/game/dungeon-monster-chain-warden-v1.webp",
  threeMawBasilisk: "/assets/game/dungeon-monster-three-maw-basilisk-v3.webp",
} as const;

export const DUNGEON_PROP_ASSETS = {
  cache: "/assets/game/dungeon-relic-cache-v4.webp",
  trap: "/assets/game/dungeon-rune-trap-v4.webp",
} as const;

export const DUNGEON_SPRITE_IDS = [
  "hero-back",
  "monster-brute",
  "monster-raider",
  "monster-stalker",
  "monster-warden",
  "trap-spikes",
  "relic-cache",
] as const;

export type GameLaunchChampionId =
  | "wizard"
  | "ape"
  | "ninja"
  | "robot"
  | "dragon";

export type GameChampionAnimationState =
  | "idle"
  | "attack-light"
  | "attack-heavy"
  | "ability"
  | "hit"
  | "victory"
  | "defeat";

export type GameChampionAnimationEvent =
  | "typed-letter"
  | "word-completed"
  | "ability-used"
  | "core-hit"
  | "run-won"
  | "run-lost"
  | "animation-finished";

export type GameHudIconId =
  | "hud-integrity"
  | "hud-wave"
  | "hud-objective"
  | "hud-score"
  | "hud-combo"
  | "hud-coin"
  | "hud-pause";

export type GameAbilityIconId =
  | "ability-blast"
  | "ability-wall"
  | "ability-freeze"
  | "ability-repel"
  | "ability-repair";

export type GameWordStateIconId =
  | "word-neutral"
  | "word-matched"
  | "word-targeted"
  | "word-danger"
  | "word-shielded"
  | "word-chain-locked";

export type GameProjectileIconId =
  | "projectile-arcane-spark"
  | "projectile-arcane-prism"
  | "projectile-arcane-shard"
  | "projectile-vanguard-shard"
  | "projectile-veil-star"
  | "projectile-veil-slash"
  | "projectile-architect-pulse"
  | "projectile-architect-cross"
  | "projectile-ember-dart"
  | "projectile-ember-star"
  | "projectile-future-sigil";

export type GameIconId =
  | GameHudIconId
  | GameAbilityIconId
  | GameWordStateIconId
  | GameProjectileIconId;

export interface GameArenaAsset {
  id: "astral-vault" | "ember-observatory";
  name: string;
  assetPath: string;
  lowIntensityAssetPath: string;
  accessibleDescription: string;
}

export interface GameChampionAssetFamily {
  id: GameLaunchChampionId;
  portraitPath: string;
  arenaId: GameArenaAsset["id"];
  projectileIds: GameProjectileIconId[];
  effectFamily: string;
  animationStates: GameChampionAnimationState[];
}

export interface GameAudioCueDefinition {
  id:
    | "navigate"
    | "confirm"
    | "type"
    | "error"
    | "complete"
    | "danger"
    | "critical"
    | "wave"
    | "upgrade"
    | "pause"
    | "victory"
    | "defeat";
  family: "navigation" | "confirmation" | "warning" | "completion";
  frequencies: readonly number[];
  durationMs: number;
  waveform: OscillatorType;
  visualEquivalent: string;
  minimumIntervalMs: number;
}

export interface GameMusicSceneDefinition {
  id: "menu" | "run" | "pressure" | "results";
  frequencies: readonly number[];
  waveforms: readonly OscillatorType[];
  gain: number;
}

const ALL_ANIMATION_STATES: GameChampionAnimationState[] = [
  "idle",
  "attack-light",
  "attack-heavy",
  "ability",
  "hit",
  "victory",
  "defeat",
];

export const GAME_ARENA_ASSETS: GameArenaAsset[] = [
  {
    id: "astral-vault",
    name: "Astral Vault",
    assetPath: "/assets/game/arena-astral-vault-v1.svg",
    lowIntensityAssetPath: "/assets/game/arena-astral-vault-low-v1.svg",
    accessibleDescription:
      "A cool blue circular observatory with restrained arcane markings.",
  },
  {
    id: "ember-observatory",
    name: "Ember Observatory",
    assetPath: "/assets/game/arena-ember-observatory-v1.svg",
    lowIntensityAssetPath: "/assets/game/arena-ember-observatory-low-v1.svg",
    accessibleDescription:
      "A warm copper observatory with geometric forge markings.",
  },
];

export const GAME_CHAMPION_ASSET_FAMILIES: GameChampionAssetFamily[] = [
  {
    id: "wizard",
    portraitPath: "/assets/game/champion-wizard.svg",
    arenaId: "astral-vault",
    projectileIds: [
      "projectile-arcane-spark",
      "projectile-arcane-prism",
      "projectile-arcane-shard",
    ],
    effectFamily: "Arcane lattice",
    animationStates: [...ALL_ANIMATION_STATES],
  },
  {
    id: "ape",
    portraitPath: "/assets/game/champion-ape.svg",
    arenaId: "ember-observatory",
    projectileIds: ["projectile-vanguard-shard"],
    effectFamily: "Seismic fracture",
    animationStates: [...ALL_ANIMATION_STATES],
  },
  {
    id: "ninja",
    portraitPath: "/assets/game/champion-ninja.svg",
    arenaId: "astral-vault",
    projectileIds: ["projectile-veil-star", "projectile-veil-slash"],
    effectFamily: "Veil cut",
    animationStates: [...ALL_ANIMATION_STATES],
  },
  {
    id: "robot",
    portraitPath: "/assets/game/champion-robot.svg",
    arenaId: "astral-vault",
    projectileIds: [
      "projectile-architect-pulse",
      "projectile-architect-cross",
    ],
    effectFamily: "Magnetic grid",
    animationStates: [...ALL_ANIMATION_STATES],
  },
  {
    id: "dragon",
    portraitPath: "/assets/game/champion-dragon.svg",
    arenaId: "ember-observatory",
    projectileIds: ["projectile-ember-dart", "projectile-ember-star"],
    effectFamily: "Ember line",
    animationStates: [...ALL_ANIMATION_STATES],
  },
];

export const GAME_HUD_ICON_IDS: GameHudIconId[] = [
  "hud-integrity",
  "hud-wave",
  "hud-objective",
  "hud-score",
  "hud-combo",
  "hud-coin",
  "hud-pause",
];

export const GAME_ABILITY_ICON_IDS: GameAbilityIconId[] = [
  "ability-blast",
  "ability-wall",
  "ability-freeze",
  "ability-repel",
  "ability-repair",
];

export const GAME_WORD_STATE_ICON_IDS: GameWordStateIconId[] = [
  "word-neutral",
  "word-matched",
  "word-targeted",
  "word-danger",
  "word-shielded",
  "word-chain-locked",
];

export const GAME_AUDIO_CUES: GameAudioCueDefinition[] = [
  { id: "navigate", family: "navigation", frequencies: [392], durationMs: 45, waveform: "sine", visualEquivalent: "Visible focus and selection state", minimumIntervalMs: 35 },
  { id: "confirm", family: "confirmation", frequencies: [466, 622], durationMs: 90, waveform: "sine", visualEquivalent: "Pressed or selected control state", minimumIntervalMs: 80 },
  { id: "type", family: "confirmation", frequencies: [520], durationMs: 35, waveform: "triangle", visualEquivalent: "Typed prefix appears in the input dock", minimumIntervalMs: 20 },
  { id: "error", family: "warning", frequencies: [145], durationMs: 80, waveform: "sine", visualEquivalent: "No-match input state and explanatory text", minimumIntervalMs: 90 },
  { id: "complete", family: "completion", frequencies: [740, 988], durationMs: 120, waveform: "sine", visualEquivalent: "Word impact and immediate removal", minimumIntervalMs: 55 },
  { id: "danger", family: "warning", frequencies: [196], durationMs: 160, waveform: "triangle", visualEquivalent: "Danger plate pattern and threat notice", minimumIntervalMs: 1200 },
  { id: "critical", family: "warning", frequencies: [164, 123], durationMs: 260, waveform: "triangle", visualEquivalent: "Critical integrity pattern, meter, and status message", minimumIntervalMs: 2500 },
  { id: "wave", family: "completion", frequencies: [392, 523, 659], durationMs: 280, waveform: "sine", visualEquivalent: "Wave banner and progress reset", minimumIntervalMs: 500 },
  { id: "upgrade", family: "confirmation", frequencies: [523, 659, 784], durationMs: 260, waveform: "triangle", visualEquivalent: "Upgrade card confirmation and loadout update", minimumIntervalMs: 250 },
  { id: "pause", family: "navigation", frequencies: [247], durationMs: 100, waveform: "sine", visualEquivalent: "Pause overlay and frozen arena state", minimumIntervalMs: 120 },
  { id: "victory", family: "completion", frequencies: [392, 523, 659, 784], durationMs: 420, waveform: "sine", visualEquivalent: "Victory champion pose and mission debrief", minimumIntervalMs: 1000 },
  { id: "defeat", family: "warning", frequencies: [247, 196, 147], durationMs: 420, waveform: "triangle", visualEquivalent: "Defeat champion pose and core-breach debrief", minimumIntervalMs: 1000 },
];

export const GAME_MUSIC_SCENES: GameMusicSceneDefinition[] = [
  { id: "menu", frequencies: [98, 146.83], waveforms: ["sine", "triangle"], gain: 0.42 },
  { id: "run", frequencies: [82.41, 123.47], waveforms: ["sine", "triangle"], gain: 0.52 },
  { id: "pressure", frequencies: [164.81, 246.94], waveforms: ["triangle", "sine"], gain: 0.2 },
  { id: "results", frequencies: [130.81, 196, 261.63], waveforms: ["sine", "triangle", "sine"], gain: 0.34 },
];

export const GAME_ASSET_METADATA = {
  version: GAME_ASSET_VERSION,
  authorship: "Original MasterTyping production vectors, illustrated dungeon art, and procedural audio",
  license: "Copyright Sixsmith Games; distributed with MasterTyping",
  iconSpritePath: GAME_ICON_SPRITE_PATH,
  dungeonSpritePath: DUNGEON_SPRITE_PATH,
  dungeonEnvironmentPath: DUNGEON_ENVIRONMENT_PATH,
  dungeonCharacterAssets: DUNGEON_CHARACTER_ASSETS,
  dungeonPropAssets: DUNGEON_PROP_ASSETS,
  highDpi: true,
  bakedInText: false,
  externalDependencies: false,
} as const;

export function getChampionAnimationState(
  current: GameChampionAnimationState,
  event: GameChampionAnimationEvent,
): { state: GameChampionAnimationState; durationMs: number; terminal: boolean } {
  if (current === "victory" || current === "defeat") {
    return { state: current, durationMs: 0, terminal: true };
  }
  const transitions: Record<
    Exclude<GameChampionAnimationEvent, "animation-finished">,
    { state: GameChampionAnimationState; durationMs: number }
  > = {
    "typed-letter": { state: "attack-light", durationMs: 180 },
    "word-completed": { state: "attack-heavy", durationMs: 360 },
    "ability-used": { state: "ability", durationMs: 520 },
    "core-hit": { state: "hit", durationMs: 420 },
    "run-won": { state: "victory", durationMs: 0 },
    "run-lost": { state: "defeat", durationMs: 0 },
  };
  if (event === "animation-finished") {
    return { state: "idle", durationMs: 0, terminal: false };
  }
  const transition = transitions[event];
  return {
    ...transition,
    terminal: transition.state === "victory" || transition.state === "defeat",
  };
}

export function validateGameProductionAssetMatrix(): string[] {
  const errors: string[] = [];
  if (GAME_CHAMPION_ASSET_FAMILIES.length !== 5) {
    errors.push("Exactly five launch champion asset families are required.");
  }
  if (GAME_ARENA_ASSETS.length < 2) {
    errors.push("At least two arena themes are required.");
  }
  for (const champion of GAME_CHAMPION_ASSET_FAMILIES) {
    if (new Set(champion.animationStates).size !== ALL_ANIMATION_STATES.length) {
      errors.push(`${champion.id} is missing a required animation state.`);
    }
    if (champion.projectileIds.length === 0) {
      errors.push(`${champion.id} is missing a projectile family.`);
    }
  }
  for (const family of ["navigation", "confirmation", "warning", "completion"] as const) {
    if (!GAME_AUDIO_CUES.some((cue) => cue.family === family)) {
      errors.push(`The ${family} audio family is missing.`);
    }
  }
  for (const scene of ["menu", "run", "pressure", "results"] as const) {
    if (!GAME_MUSIC_SCENES.some((entry) => entry.id === scene)) {
      errors.push(`The ${scene} music scene is missing.`);
    }
  }
  return errors;
}
